'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { Check, X } from 'lucide-react'

// Building blocks several demos share: a checklist that ticks as a story
// plays, the outcome stamp, and an SMS/chat thread.

/** Checklist that ticks items off as `step` reaches each item's `at`. */
export function Trail({ title, items, step, tone = 'good' }) {
  return (
    <div className="svd-trail">
      {title ? <span className="svd-trail-title">{title}</span> : null}
      <ol>
        {items.map((item) => {
          const done = step >= item.at
          const itemTone = item.tone || tone
          return (
            <li key={item.text} className={done ? `is-done is-${itemTone}` : ''}>
              <span className="svd-trail-mark" aria-hidden="true">
                {done ? (itemTone === 'bad' ? <X size={12} strokeWidth={3} /> : itemTone === 'wait' ? '!' : <Check size={12} strokeWidth={3} />) : null}
              </span>
              {item.text}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/** The outcome badge that lands on the card's corner when a story ends. */
export function ResultStamp({ show, tone, text, id }) {
  return (
    <AnimatePresence>
      {show ? (
        <motion.span
          key={id}
          className={`svd-result is-${tone}`}
          initial={{ opacity: 0, y: 12, rotate: -4, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, rotate: -3, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        >
          {tone === 'good' ? <Check size={16} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}
          {text}
        </motion.span>
      ) : null}
    </AnimatePresence>
  )
}

/** Message bubbles; `from` is 'them' (left) or 'us' (right). */
export function Thread({ messages, id, children }) {
  return (
    <div className="svd-thread" aria-live="polite">
      <AnimatePresence initial={false}>
        {messages.map((m, i) => (
          <motion.p
            key={`${id}-${i}`}
            className={`svd-bubble ${m.from === 'us' ? 'is-bot' : 'is-lead'}`}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            {m.meta ? <small className="svd-bubble-meta">{m.meta}</small> : null}
            {m.text}
          </motion.p>
        ))}
      </AnimatePresence>
      {children}
    </div>
  )
}
